import { getCustomContactProperties, CustomContactSchema } from '@/configs/custom-contact-properties';
import {
  getCustomOrderProperties,
  getCustomCartProperties,
  getCustomProductProperties,
  CustomEntitySchema,
} from '@/configs/custom-entity-properties';
import { fetchCustomSchemas } from '@/composables/custom-data/customData';
import type { CollectionGroup, CollectionOption } from '@/types/loop-configuration';

/**
 * Get all available collections for looping
 * Includes native collections and custom schema collections
 * Base contexts: contact, order, cart, visitor
 * Additional contexts can be added (e.g., product for smart product lists)
 * Collections are organized by context (like properties in conditions)
 */
export const getAvailableCollections = async (
  contexts: string[] = ['contact', 'order', 'cart', 'visitor'],
): Promise<CollectionGroup[]> => {
  const groups: CollectionGroup[] = [];

  // Load all custom schemas in parallel
  const schemaPromises = contexts.map(async (context) => {
    let customSchemas: (CustomContactSchema | CustomEntitySchema)[] = [];

    if (context === 'contact') {
      customSchemas = await getCustomContactProperties();
    } else if (context === 'order') {
      customSchemas = await getCustomOrderProperties();
    } else if (context === 'cart') {
      customSchemas = await getCustomCartProperties();
    } else if (context === 'product') {
      customSchemas = await getCustomProductProperties();
    }

    return { context, customSchemas };
  });

  const results = await Promise.all(schemaPromises);

  // Build groups by context
  const contextGroups: Record<string, CollectionOption[]> = {
    contact: [],
    order: [],
    cart: [],
    visitor: [],
    product: [],
  };

  // Add native collections to their respective contexts
  // Contact collections
  // TODO : add contact collections
  // contextGroups.contact.push(
  //   {
  //     label: 'Commandes du contact',
  //     value: 'contact.orders',
  //     defaultIterator: 'order',
  //     category: 'native',
  //     isCustom: false,
  //   },
  //   {
  //     label: 'Paniers du contact',
  //     value: 'contact.carts',
  //     defaultIterator: 'cart',
  //     category: 'native',
  //     isCustom: false,
  //   },
  // );

  // Order collections
  // if (contexts.includes('order')) {
  //   contextGroups.order.push({
  //     label: 'Produits de la commande',
  //     value: 'order.products',
  //     defaultIterator: 'product',
  //     isCustom: false,
  //   });
  // }

  // Cart collections
  // if (contexts.includes('cart')) {
  //   contextGroups.cart.push({
  //     label: 'Produits du panier',
  //     value: 'cart.products',
  //     defaultIterator: 'product',
  //     isCustom: false,
  //   });
  // }

  // Add custom collections to their respective contexts
  try {
    results.forEach(({ context, customSchemas }) => {
      customSchemas.forEach((schema: CustomContactSchema | CustomEntitySchema) => {
        // The schema itself is a collection (e.g., customer_addresses is a collection of addresses)
        const schemaNameLower = schema.schemaName.toLowerCase().replace(/\s+/g, '_');

        if (contextGroups[context]) {
          contextGroups[context].push({
            label: schema.schemaName,
            value: `${context}.custom.${schemaNameLower}`,
            defaultIterator: schemaNameLower.endsWith('s') ? schemaNameLower.slice(0, -1) : schemaNameLower,
            schemaName: schema.schemaName,
            isCustom: true,
          });
        }
      });
    });
  } catch (error) {
    console.error('Error loading custom collections:', error);
  }

  // Add standalone custom schemas (schemas without relationships to system entities)
  const standaloneCollections: CollectionOption[] = [];
  try {
    const allSchemas = await fetchCustomSchemas();

    // Filter schemas that have no relationships or no system relationships
    const standaloneSchemas = allSchemas.filter((schema) => {
      if (!schema.relationships || schema.relationships.length === 0) {
        return true;
      }

      // Check if all relationships are to custom schemas (not system)
      const hasSystemRelationship = schema.relationships.some(
        (rel) => rel.targetSchemaType === 'system',
      );

      return !hasSystemRelationship;
    });

    standaloneSchemas.forEach((schema) => {
      const schemaNameLower = schema.schema_name.toLowerCase().replace(/\s+/g, '_');

      standaloneCollections.push({
        label: schema.schema_name,
        value: `custom.${schemaNameLower}`,
        defaultIterator: schemaNameLower.endsWith('s') ? schemaNameLower.slice(0, -1) : schemaNameLower,
        schemaName: schema.schema_name,
        isCustom: true,
      });
    });
  } catch (error) {
    console.error('Error loading standalone collections:', error);
  }

  // Build final groups array with context labels
  const contextLabels: Record<string, string> = {
    contact: 'Contact',
    order: 'Commande',
    cart: 'Panier',
    visitor: 'Visiteur',
    product: 'Produit',
  };

  contexts.forEach((context) => {
    if (contextGroups[context] && contextGroups[context].length > 0) {
      groups.push({
        label: contextLabels[context] || context,
        items: contextGroups[context],
      });
    }
  });

  // Add standalone collections group if any exist
  if (standaloneCollections.length > 0) {
    groups.push({
      label: 'Collections standalone',
      items: standaloneCollections,
    });
  }

  return groups;
};

/**
 * Get default iterator name for a collection
 */
export const getDefaultIterator = (collectionPath: string): string => {
  const parts = collectionPath.split('.');
  const lastPart = parts[parts.length - 1];

  // Remove 's' at the end if present
  if (lastPart.endsWith('s')) {
    return lastPart.slice(0, -1);
  }

  return lastPart;
};
